/**
 * 图片代理核心功能
 * 支持负载均衡和多种代理服务
 */

import { ServiceType, ImageProxyOptions } from "./types";
import { LoadBalancer } from "./loadBalancer";

// 代理服务URL配置
const PROXY_SERVICES = {
  // 现有的代理服务URL
  EXISTING: "https://edgen-service.pages.dev/api/proxy-image",
  // 新的代理服务URL
  NEW: "https://image-service.edwealth.ai",
} as const;

// 创建负载均衡器实例
const loadBalancer = new LoadBalancer();

/**
 * 生成代理图片URL（支持负载均衡）
 * @param imageUrl 原始图片URL
 * @param options 配置选项
 * @returns 代理后的图片URL
 */
export function proxyImage(
  imageUrl: string,
  options: ImageProxyOptions = {},
): string {
  if (!imageUrl) {
    return "";
  }

  // 如果已经是代理URL，直接返回
  if (isProxyUrl(imageUrl)) {
    return imageUrl;
  }

  // 如果指定了服务类型，直接使用
  if (options.serviceType) {
    return buildProxyUrl(imageUrl, options.serviceType);
  }

  // 根据负载均衡策略选择服务
  const algorithm = options.algorithm || "weighted_random";
  const selectedService = loadBalancer.selectService(algorithm);

  return buildProxyUrl(imageUrl, selectedService);
}

/**
 * 批量生成代理图片URL（支持负载均衡）
 * @param imageUrls 图片URL数组
 * @param options 配置选项
 * @returns 代理后的图片URL数组
 */
export function proxyImages(
  imageUrls: string[],
  options: ImageProxyOptions = {},
): string[] {
  return imageUrls.map((url) => proxyImage(url, options));
}

/**
 * 手动选择服务类型（用于测试或特殊情况）
 * @param imageUrl 原始图片URL
 * @param serviceType 指定的服务类型
 * @returns 代理后的图片URL
 */
export function proxyImageWithService(
  imageUrl: string,
  serviceType: ServiceType,
): string {
  if (!imageUrl) {
    return "";
  }

  // 如果已经是代理URL，直接返回
  if (isProxyUrl(imageUrl)) {
    return imageUrl;
  }

  return buildProxyUrl(imageUrl, serviceType);
}

/**
 * 构建代理URL
 * @param imageUrl 原始图片URL
 * @param serviceType 服务类型
 * @returns 代理URL
 */
function buildProxyUrl(imageUrl: string, serviceType: ServiceType): string {
  if (serviceType === "existing") {
    return `${PROXY_SERVICES.EXISTING}?url=${imageUrl}`;
  } else {
    return `${PROXY_SERVICES.NEW}/?imageUrl=${imageUrl}`;
  }
}

/**
 * 检查是否为代理URL
 * @param url 要检查的URL
 * @returns 是否为代理URL
 */
export function isProxyUrl(url: string): boolean {
  return (
    url.includes(PROXY_SERVICES.EXISTING) || url.includes(PROXY_SERVICES.NEW)
  );
}

/**
 * 从代理URL中提取原始URL
 * @param proxyUrl 代理URL
 * @returns 原始URL或空字符串
 */
export function extractOriginalUrl(proxyUrl: string): string {
  if (!isProxyUrl(proxyUrl)) {
    return proxyUrl;
  }

  try {
    const url = new URL(proxyUrl);

    // 检查是否为现有服务格式
    if (proxyUrl.includes(PROXY_SERVICES.EXISTING)) {
      return url.searchParams.get("url") || "";
    }

    // 检查是否为新服务格式
    if (proxyUrl.includes(PROXY_SERVICES.NEW)) {
      return url.searchParams.get("imageUrl") || "";
    }

    return "";
  } catch {
    return "";
  }
}

/**
 * 获取负载均衡器统计信息
 * @returns 负载均衡器统计信息
 */
export function getLoadBalancerStats() {
  return loadBalancer.getStats();
}

/**
 * 获取所有可用的代理服务URL
 * @returns 代理服务URL数组
 */
export function getAvailableProxyServices(): string[] {
  return [PROXY_SERVICES.EXISTING, PROXY_SERVICES.NEW];
}

/**
 * 获取代理服务配置
 * @returns 代理服务配置信息
 */
export function getProxyServiceConfig() {
  return {
    services: PROXY_SERVICES,
    loadBalancer: loadBalancer.getConfig(),
  };
}

/**
 * 重置负载均衡器状态
 */
export function resetLoadBalancer(): void {
  loadBalancer.resetCount();
}

/**
 * 验证图片URL格式
 * @param url 要验证的URL
 * @returns 是否为有效的图片URL
 */
export function isValidImageUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    const validProtocols = ["http:", "https:"];
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".avif"];

    // 检查协议
    if (!validProtocols.includes(urlObj.protocol)) {
      return false;
    }

    // 检查文件扩展名
    const hasValidExtension = validExtensions.some((ext) =>
      urlObj.pathname.toLowerCase().endsWith(ext),
    );

    return hasValidExtension;
  } catch {
    return false;
  }
}

/**
 * 批量验证图片URL
 * @param urls 图片URL数组
 * @returns 验证结果对象
 */
export function validateImageUrls(urls: string[]): {
  valid: string[];
  invalid: string[];
  total: number;
  validCount: number;
  invalidCount: number;
} {
  const valid: string[] = [];
  const invalid: string[] = [];

  urls.forEach((url) => {
    if (isValidImageUrl(url)) {
      valid.push(url);
    } else {
      invalid.push(url);
    }
  });

  return {
    valid,
    invalid,
    total: urls.length,
    validCount: valid.length,
    invalidCount: invalid.length,
  };
}
